/**
 * Get the start of the week (Monday) for a given date
 * @param {Date} date - Reference date
 * @returns {Date} Start of week (Monday, 00:00 UTC)
 */
export const getStartOfWeek = (date) => {
  const dayOfWeek = date.getUTCDay();
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const startOfWeek = new Date(date);
  startOfWeek.setUTCDate(date.getUTCDate() + diffToMonday);
  startOfWeek.setUTCHours(0, 0, 0, 0);
  return startOfWeek;
};

/**
 * Get the end of the week (Sunday) for a given date
 * @param {Date} date - Reference date
 * @returns {Date} End of week (Sunday, 23:59 UTC)
 */
export const getEndOfWeek = (date) => {
  const startOfWeek = getStartOfWeek(date);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setUTCDate(startOfWeek.getUTCDate() + 6);
  endOfWeek.setUTCHours(23, 59, 59, 999);
  return endOfWeek;
};

/**
 * Get week range label (e.g., "Feb 2 - Feb 8")
 * @param {number} weekOffset - Offset from current week (0 = this week, -1 = last week, etc.)
 * @returns {string} Week range label
 */
export const getWeekRangeLabel = (weekOffset = 0) => {
  const now = new Date();
  const targetDate = new Date(now.getTime() + weekOffset * 7 * 24 * 60 * 60 * 1000);
  const startOfWeek = getStartOfWeek(targetDate);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setUTCDate(startOfWeek.getUTCDate() + 6);
  
  const startStr = startOfWeek.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  });
  const endStr = endOfWeek.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  });
  
  return `${startStr} - ${endStr}`;
};

/**
 * Get date string in YYYY-MM-DD format (UTC)
 * @param {Date} date - Date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const getDateString = (date) => {
  return date.toISOString().split('T')[0];
};

/**
 * Parse time string (HH:MM) into hours and minutes
 * @param {string} timeStr - Time string in HH:MM format
 * @returns {object} Object with hours and minutes properties
 */
export const parseTimeString = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return { hours, minutes };
};

/**
 * Calculate days until a specific day of the week and time
 * @param {number} targetDayIndex - Target day index (0 = Sunday, 6 = Saturday)
 * @param {number} targetHours - Target hour (0-23)
 * @param {number} targetMinutes - Target minutes (0-59)
 * @returns {number} Milliseconds until target
 */
export const msUntilNextDayTime = (targetDayIndex, targetHours, targetMinutes) => {
  const now = new Date();
  const currentUTCDay = now.getUTCDay();
  
  const targetDate = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    targetHours,
    targetMinutes,
    0,
    0
  ));
  
  let daysUntil = targetDayIndex - currentUTCDay;
  if (daysUntil < 0) daysUntil += 7;
  if (daysUntil === 0 && targetDate <= now) daysUntil = 7;
  
  targetDate.setUTCDate(targetDate.getUTCDate() + daysUntil);
  
  return Math.max(0, targetDate - now);
};

/**
 * Format time remaining in human-readable format
 * @param {number} milliseconds - Milliseconds remaining
 * @returns {string} Human-readable time (e.g., "2h 30m", "NOW!")
 */
export const formatTimeRemaining = (milliseconds) => {
  const totalMinutes = Math.floor(milliseconds / 60000);
  
  if (totalMinutes < 0) return 'NOW!';
  
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  
  if (days >= 1) {
    return `${days}d ${hours}h`;
  } else if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  } else {
    return `${minutes}m`;
  }
};

/**
 * Check if a date is today
 * @param {string|Date} date - Date to check (YYYY-MM-DD or Date object)
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
  return dateStr === todayStr;
};

export const getWeekRangeForDate = (date, endDate) => {
  if (!date) {
    return 'TBD';
  }
  
  let startDateObj, endDateObj;
  
  if (typeof date === 'string') {
    startDateObj = new Date(date);
  } else {
    startDateObj = new Date(date);
  }
  
  // Check if start date is invalid
  if (isNaN(startDateObj.getTime())) {
    return 'TBD';
  }
  
  // If end date provided, use it; otherwise calculate Monday-Sunday week
  if (endDate) {
    if (typeof endDate === 'string') {
      endDateObj = new Date(endDate);
    } else {
      endDateObj = new Date(endDate);
    }
    
    if (isNaN(endDateObj.getTime())) {
      endDateObj = startDateObj;
    }
  } else {
    const startOfWeek = getStartOfWeek(startDateObj);
    endDateObj = new Date(startOfWeek);
    endDateObj.setUTCDate(startOfWeek.getUTCDate() + 6);
  }
  
  // Use Intl.DateTimeFormat to display in user's local timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric'
  });
  
  const startStr = formatter.format(startDateObj);
  const endStr = formatter.format(endDateObj);
  
  return `${startStr} - ${endStr}`;
};
